import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import blogImg from "../assests/images/blog-bg.jpg";
import axios from "axios";
import { Helmet } from "react-helmet-async";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);

      try {
        const res = await axios.get(
          "https://dubaihotelrates.com/api/blogs"
        );

        const data = res.data;

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((b) => b.category)
              .map((b) => [b.category.id, b.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = selectedCategory
      ? blog.category?.id === Number(selectedCategory)
      : true;

    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <>
      {/* =========================================================
                SEO
            ========================================================= */}
      <Helmet>
        <title>
          Travel Blogs & Hotel Guides | Beach Hotels Dubai
        </title>

        <meta
          name="description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Beach Hotels Dubai."
        />

        <link
          rel="canonical"
          href="https://dubaihotelrates.com/blogs"
        />

        <meta
          property="og:title"
          content="Travel Blogs & Hotel Guides | Beach Hotels Dubai"
        />

        <meta
          property="og:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Beach Hotels Dubai."
        />

        <meta
          property="og:url"
          content="https://dubaihotelrates.com/blogs"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Travel Blogs & Hotel Guides | Beach Hotels Dubai"
        />

        <meta
          name="twitter:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Beach Hotels Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>


      <div className="w-full overflow-hidden bg-white">

        {/* =====================================================
                    HERO
                ====================================================== */}

        <Hero
          title="Travel Stories"
          subtitle="Discover Dubai through guides, ideas, hotel tips and travel inspiration."
          backgroundImage={blogImg}
        />


        {/* =====================================================
                    EDITORIAL INTRO
                ====================================================== */}

        <section className="relative overflow-hidden bg-[#F7FAFF]">

          {/* Decorative background */}
          <div className="
                        pointer-events-none
                        absolute
                        -left-40
                        top-20
                        h-96
                        w-96
                        rounded-full
                        bg-[#174EA6]/5
                        blur-3xl
                    " />

          <div className="
                        pointer-events-none
                        absolute
                        -right-40
                        top-80
                        h-96
                        w-96
                        rounded-full
                        bg-[#FFC928]/10
                        blur-3xl
                    " />


          <div className="
                        relative
                        mx-auto
                        max-w-7xl
                        px-4
                        sm:px-6
                        lg:px-8
                    ">

            {/* =================================================
                            INTRO
                        ================================================= */}

            <div className="
                            flex
                            flex-col
                            gap-7
                            pb-10
                            pt-14
                            sm:pb-12
                            sm:pt-16
                            lg:flex-row
                            lg:items-end
                            lg:justify-between
                        ">

              <div className="max-w-2xl">

                <div className="
                                    mb-5
                                    flex
                                    items-center
                                    gap-3
                                ">

                  <span className="
                                        h-[2px]
                                        w-10
                                        bg-[#FFC928]
                                    " />

                  <span className="
                                        text-xs
                                        font-bold
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#174EA6]
                                    ">
                    Beach Hotels Dubai
                  </span>

                </div>

                <h1 className="
                                    text-3xl
                                    font-extrabold
                                    leading-tight
                                    tracking-tight
                                    text-[#0F172A]
                                    sm:text-4xl
                                    lg:text-5xl
                                ">
                  Stories for your
                  <span className="text-[#174EA6]">
                    {" "}next journey
                  </span>
                </h1>

                <p className="
                                    mt-4
                                    max-w-xl
                                    text-sm
                                    leading-7
                                    text-slate-500
                                    sm:text-base
                                ">
                  Explore Dubai travel guides, hotel
                  recommendations, destination ideas and
                  useful tips designed to help you travel
                  smarter.
                </p>

              </div>


              {/* Article counter */}
              {!loading && blogs.length > 0 && (
                <div className="
                                    flex
                                    shrink-0
                                    items-center
                                    gap-3
                                    rounded-2xl
                                    border
                                    border-slate-200
                                    bg-white
                                    px-5
                                    py-4
                                    shadow-sm
                                ">

                  <div className="
                                        flex
                                        h-11
                                        w-11
                                        items-center
                                        justify-center
                                        rounded-xl
                                        bg-[#EAF3FF]
                                        text-[#174EA6]
                                    ">
                    <FaClock />
                  </div>

                  <div>
                    <p className="
                                            text-xl
                                            font-extrabold
                                            leading-none
                                            text-[#0F172A]
                                        ">
                      {blogs.length}
                    </p>

                    <p className="
                                            mt-1
                                            text-xs
                                            text-slate-500
                                        ">
                      Travel stories
                    </p>
                  </div>

                </div>
              )}

            </div>


            {/* =================================================
                            SEARCH + CATEGORIES
                        ================================================= */}

            <div className="pb-10">

              <div className="
                                rounded-[26px]
                                border
                                border-slate-200
                                bg-white
                                p-3
                                shadow-[0_15px_45px_rgba(15,23,42,0.06)]
                                sm:p-4
                            ">

                <div className="
                                    flex
                                    flex-col
                                    gap-4
                                    lg:flex-row
                                    lg:items-center
                                ">

                  {/* Search */}
                  <div className="
                                        relative
                                        flex-1
                                    ">

                    <FaSearch className="
                                            absolute
                                            left-4
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        " />

                    <input
                      type="text"
                      value={search}
                      onChange={(e) =>
                        setSearch(e.target.value)
                      }
                      placeholder="Search travel stories..."
                      className="
                                                w-full
                                                rounded-xl
                                                border
                                                border-slate-200
                                                bg-[#F7FAFF]
                                                py-3.5
                                                pl-11
                                                pr-11
                                                text-sm
                                                text-slate-800
                                                outline-none
                                                transition
                                                placeholder:text-slate-400
                                                focus:border-[#174EA6]
                                                focus:ring-4
                                                focus:ring-[#174EA6]/10
                                            "
                    />

                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="
                                                    absolute
                                                    right-3
                                                    top-1/2
                                                    flex
                                                    h-7
                                                    w-7
                                                    -translate-y-1/2
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    bg-slate-100
                                                    text-slate-400
                                                    transition
                                                    hover:bg-[#EAF3FF]
                                                    hover:text-[#174EA6]
                                                "
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    )}

                  </div>


                  {/* Category pills */}
                  <div className="
                                        flex
                                        gap-2
                                        overflow-x-auto
                                        pb-1
                                        lg:max-w-[58%]
                                        lg:pb-0
                                    ">

                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCategory("")
                      }
                      className={`
                                                shrink-0
                                                rounded-full
                                                px-4
                                                py-2.5
                                                text-xs
                                                font-bold
                                                transition-all
                                                duration-200
                                                ${selectedCategory === ""
                          ? "bg-[#174EA6] text-white shadow-md"
                          : "bg-slate-100 text-slate-600 hover:bg-[#EAF3FF] hover:text-[#174EA6]"
                        }
                                            `}
                    >
                      All Stories
                    </button>


                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() =>
                          setSelectedCategory(
                            String(cat.id)
                          )
                        }
                        className={`
                                                    shrink-0
                                                    rounded-full
                                                    px-4
                                                    py-2.5
                                                    text-xs
                                                    font-bold
                                                    transition-all
                                                    duration-200
                                                    ${selectedCategory ===
                            String(cat.id)
                            ? "bg-[#174EA6] text-white shadow-md"
                            : "bg-slate-100 text-slate-600 hover:bg-[#EAF3FF] hover:text-[#174EA6]"
                          }
                                                `}
                      >
                        {cat.name}
                      </button>
                    ))}

                  </div>

                </div>

              </div>

            </div>


            {/* =================================================
                            CONTENT
                        ================================================= */}

            {loading ? (

              /* =================================================
                  LOADING
              ================================================= */

              <div className="
                                grid
                                grid-cols-1
                                gap-6
                                pb-20
                                md:grid-cols-2
                                lg:grid-cols-3
                            ">

                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div
                    key={item}
                    className="
                                            overflow-hidden
                                            rounded-[28px]
                                            border
                                            border-slate-100
                                            bg-white
                                            shadow-sm
                                        "
                  >

                    <div className="
                                            h-60
                                            animate-pulse
                                            bg-slate-200
                                        " />

                    <div className="p-6">

                      <div className="
                                                h-3
                                                w-24
                                                animate-pulse
                                                rounded
                                                bg-slate-200
                                            " />

                      <div className="
                                                mt-4
                                                h-6
                                                w-full
                                                animate-pulse
                                                rounded
                                                bg-slate-200
                                            " />

                      <div className="
                                                mt-2
                                                h-6
                                                w-4/5
                                                animate-pulse
                                                rounded
                                                bg-slate-200
                                            " />

                      <div className="
                                                mt-5
                                                h-4
                                                w-full
                                                animate-pulse
                                                rounded
                                                bg-slate-200
                                            " />

                      <div className="
                                                mt-2
                                                h-4
                                                w-2/3
                                                animate-pulse
                                                rounded
                                                bg-slate-200
                                            " />

                    </div>

                  </div>
                ))}

              </div>

            ) : filteredBlogs.length > 0 ? (

              <>

                {/* =================================================
                                    FEATURED STORY
                                ================================================= */}

                <section className="pb-14 sm:pb-16">

                  <Link
                    to={`/blogs/${filteredBlogs[0].slug}`}
                    className="group block"
                  >

                    <div className="
                                            relative
                                            overflow-hidden
                                            rounded-[30px]
                                            bg-[#002B45]
                                            shadow-[0_25px_70px_rgba(0,43,69,0.16)]
                                        ">

                      {/* Image */}
                      <div className="
                                                relative
                                                h-[430px]
                                                sm:h-[500px]
                                                lg:h-[560px]
                                            ">

                        <img
                          src={
                            filteredBlogs[0]
                              .thumbnail_image
                          }
                          alt={
                            filteredBlogs[0].title
                          }
                          className="
                                                        absolute
                                                        inset-0
                                                        h-full
                                                        w-full
                                                        object-cover
                                                        transition-transform
                                                        duration-1000
                                                        group-hover:scale-105
                                                    "
                        />

                        {/* Overlay */}
                        <div className="
                                                    absolute
                                                    inset-0
                                                    bg-gradient-to-t
                                                    from-[#001722]
                                                    via-[#001722]/45
                                                    to-transparent
                                                " />

                      </div>


                      {/* Featured badge */}
                      <div className="
                                                absolute
                                                left-5
                                                top-5
                                                sm:left-7
                                                sm:top-7
                                            ">

                        <div className="
                                                    flex
                                                    items-center
                                                    gap-2
                                                    rounded-full
                                                    bg-[#FFC928]
                                                    px-4
                                                    py-2
                                                    text-[10px]
                                                    font-extrabold
                                                    uppercase
                                                    tracking-[0.12em]
                                                    text-[#002B45]
                                                    shadow-lg
                                                ">
                          <span className="
                                                        h-1.5
                                                        w-1.5
                                                        rounded-full
                                                        bg-[#002B45]
                                                    " />

                          Featured Story
                        </div>

                      </div>


                      {/* Content panel */}
                      <div className="
                                                absolute
                                                bottom-0
                                                left-0
                                                w-full
                                                p-5
                                                sm:p-8
                                                lg:p-10
                                            ">

                        <div className="
                                                    max-w-4xl
                                                    lg:pr-20
                                                ">

                          <div className="
                                                        flex
                                                        flex-wrap
                                                        items-center
                                                        gap-3
                                                    ">

                            {filteredBlogs[0]
                              .category && (
                                <span className="
                                                                rounded-full
                                                                border
                                                                border-white/20
                                                                bg-white/10
                                                                px-3
                                                                py-1.5
                                                                text-[10px]
                                                                font-bold
                                                                uppercase
                                                                tracking-wider
                                                                text-white
                                                                backdrop-blur-md
                                                            ">
                                  {
                                    filteredBlogs[0]
                                      .category
                                      ?.name
                                  }
                                </span>
                              )}

                            <span className="
                                                            flex
                                                            items-center
                                                            gap-2
                                                            text-xs
                                                            text-white/65
                                                        ">
                              <FaCalendarAlt className="text-[#FFC928]" />

                              {formatDate(
                                filteredBlogs[0]
                                  .created_at
                              )}
                            </span>

                          </div>


                          <h2 className="
                                                        mt-4
                                                        text-2xl
                                                        font-extrabold
                                                        leading-tight
                                                        !text-white
                                                        sm:text-3xl
                                                        lg:text-5xl
                                                    ">
                            {
                              filteredBlogs[0]
                                .title
                            }
                          </h2>


                          <p className="
                                                        mt-4
                                                        max-w-2xl
                                                        text-sm
                                                        leading-6
                                                        text-white/70
                                                        sm:text-base
                                                    ">
                            {
                              filteredBlogs[0]
                                .excerpt
                            }
                          </p>


                          <div className="
                                                        mt-6
                                                        inline-flex
                                                        items-center
                                                        gap-3
                                                        text-sm
                                                        font-bold
                                                        text-[#FFC928]
                                                    ">
                            Read Featured Story

                            <span className="
                                                            flex
                                                            h-9
                                                            w-9
                                                            items-center
                                                            justify-center
                                                            rounded-full
                                                            bg-white
                                                            text-[#174EA6]
                                                            transition-all
                                                            duration-300
                                                            group-hover:translate-x-1
                                                        ">
                              <FaArrowRight className="text-xs" />
                            </span>
                          </div>

                        </div>

                      </div>

                    </div>

                  </Link>

                </section>


                {/* =================================================
                                    LATEST STORIES HEADER
                                ================================================= */}

                <div className="
                                    mb-7
                                    flex
                                    items-end
                                    justify-between
                                    gap-4
                                ">

                  <div>

                    <div className="
                                            mb-3
                                            flex
                                            items-center
                                            gap-3
                                        ">
                      <span className="
                                                h-[2px]
                                                w-8
                                                bg-[#FFC928]
                                            " />

                      <span className="
                                                text-xs
                                                font-bold
                                                uppercase
                                                tracking-[0.18em]
                                                text-[#174EA6]
                                            ">
                        Latest Stories
                      </span>
                    </div>

                    <h2 className="
                                            text-2xl
                                            font-extrabold
                                            text-[#0F172A]
                                            sm:text-3xl
                                        ">
                      More Travel Inspiration
                    </h2>

                  </div>


                  <span className="
                                        hidden
                                        text-xs
                                        font-medium
                                        text-slate-400
                                        sm:block
                                    ">
                    {filteredBlogs.length} stories
                  </span>

                </div>


                {/* =================================================
                                    BLOG GRID
                                ================================================= */}

                <section className="
                                    grid
                                    grid-cols-1
                                    gap-6
                                    pb-20
                                    md:grid-cols-2
                                    lg:grid-cols-3
                                ">

                  {filteredBlogs.slice(1).map(
                    (blog, index) => (
                      <article
                        key={blog.id}
                        className="
                                                    group
                                                    overflow-hidden
                                                    rounded-[26px]
                                                    border
                                                    border-slate-200
                                                    bg-white
                                                    shadow-[0_8px_35px_rgba(15,23,42,0.05)]
                                                    transition-all
                                                    duration-500
                                                    hover:-translate-y-2
                                                    hover:border-[#174EA6]/20
                                                    hover:shadow-[0_20px_55px_rgba(23,78,166,0.12)]
                                                "
                      >

                        {/* Image */}
                        <Link
                          to={`/blogs/${blog.slug}`}
                          className="block"
                        >

                          <div className="
                                                        relative
                                                        h-60
                                                        overflow-hidden
                                                    ">

                            <img
                              src={
                                blog.thumbnail_image
                              }
                              alt={blog.title}
                              loading="lazy"
                              className="
                                                                h-full
                                                                w-full
                                                                object-cover
                                                                transition-transform
                                                                duration-700
                                                                group-hover:scale-110
                                                            "
                            />

                            <div className="
                                                            absolute
                                                            inset-0
                                                            bg-gradient-to-t
                                                            from-[#002B45]/60
                                                            via-transparent
                                                            to-transparent
                                                        " />


                            {/* Number */}
                            <span className="
                                                            absolute
                                                            left-4
                                                            top-4
                                                            flex
                                                            h-9
                                                            min-w-9
                                                            items-center
                                                            justify-center
                                                            rounded-full
                                                            border
                                                            border-white/20
                                                            bg-[#002B45]/70
                                                            px-2
                                                            text-xs
                                                            font-extrabold
                                                            text-white
                                                            backdrop-blur-md
                                                        ">
                              {String(
                                index + 2
                              ).padStart(
                                2,
                                "0"
                              )}
                            </span>


                            {/* Category */}
                            {blog.category && (
                              <span className="
                                                                absolute
                                                                right-4
                                                                top-4
                                                                rounded-full
                                                                bg-white/95
                                                                px-3
                                                                py-1.5
                                                                text-[10px]
                                                                font-bold
                                                                text-[#174EA6]
                                                                shadow-md
                                                                backdrop-blur
                                                            ">
                                {
                                  blog.category
                                    ?.name
                                }
                              </span>
                            )}

                          </div>

                        </Link>


                        {/* Content */}
                        <div className="p-6">

                          {/* Date */}
                          <div className="
                                                        flex
                                                        items-center
                                                        gap-2
                                                        text-[11px]
                                                        font-medium
                                                        text-slate-400
                                                    ">
                            <FaCalendarAlt className="text-[#FFC928]" />

                            {formatDate(
                              blog.created_at
                            )}
                          </div>


                          {/* Title */}
                          <Link
                            to={`/blogs/${blog.slug}`}
                          >
                            <h3 className="
                                                            mt-3
                                                            line-clamp-2
                                                            text-lg
                                                            font-extrabold
                                                            leading-snug
                                                            text-[#0F172A]
                                                            transition-colors
                                                            duration-300
                                                            group-hover:text-[#174EA6]
                                                        ">
                              {blog.title}
                            </h3>
                          </Link>


                          {/* Excerpt */}
                          <p className="
                                                        mt-3
                                                        line-clamp-3
                                                        min-h-[72px]
                                                        text-sm
                                                        leading-6
                                                        text-slate-500
                                                    ">
                            {blog.excerpt}
                          </p>


                          {/* Bottom */}
                          <div className="
                                                        mt-5
                                                        flex
                                                        items-center
                                                        justify-between
                                                        border-t
                                                        border-slate-100
                                                        pt-5
                                                    ">

                            <span className="
                                                            flex
                                                            items-center
                                                            gap-2
                                                            text-xs
                                                            font-medium
                                                            text-slate-400
                                                        ">
                              <FaClock />

                              5 min read
                            </span>


                            <Link
                              to={`/blogs/${blog.slug}`}
                              className="
                                                                inline-flex
                                                                items-center
                                                                gap-2
                                                                text-xs
                                                                font-bold
                                                                text-[#174EA6]
                                                                transition-all
                                                                duration-300
                                                                group-hover:text-[#9A7418]
                                                            "
                            >
                              Read Story

                              <FaArrowRight className="
                                                                text-[10px]
                                                                transition-transform
                                                                duration-300
                                                                group-hover:translate-x-1
                                                            " />
                            </Link>

                          </div>

                        </div>

                      </article>
                    )
                  )}

                </section>

              </>

            ) : (

              /* =================================================
                  EMPTY STATE
              ================================================= */

              <div className="
                                mb-20
                                rounded-[30px]
                                border
                                border-slate-200
                                bg-white
                                px-6
                                py-20
                                text-center
                                shadow-sm
                            ">

                <div className="
                                    mx-auto
                                    flex
                                    h-16
                                    w-16
                                    items-center
                                    justify-center
                                    rounded-2xl
                                    bg-[#EAF3FF]
                                    text-[#174EA6]
                                ">
                  <FaSearch />
                </div>

                <h3 className="
                                    mt-5
                                    text-xl
                                    font-extrabold
                                    text-[#0F172A]
                                ">
                  No stories found
                </h3>

                <p className="
                                    mx-auto
                                    mt-2
                                    max-w-md
                                    text-sm
                                    leading-6
                                    text-slate-500
                                ">
                  We couldn't find any articles matching
                  your search or selected category.
                </p>

                <button
                  type="button"
                  onClick={clearFilters}
                  className="
                                        mt-6
                                        rounded-xl
                                        bg-[#174EA6]
                                        px-5
                                        py-3
                                        text-sm
                                        font-bold
                                        text-white
                                        shadow-md
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:bg-[#002B45]
                                        hover:shadow-lg
                                    "
                >
                  View All Stories
                </button>

              </div>
            )}

          </div>

        </section>

      </div>
    </>
  );
}